import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { FileText, Plus, Trash2, Pencil } from "lucide-react";

// Fills {first_name} / {name} (customer) and {job_number} / {delivery_date} / {delivery_time} / {engineer_name} (job).
const JOB_KEYS = ["job_number", "delivery_date", "delivery_time", "engineer_name"];
const fill = (txt, name, job) => {
  const full = (name || "").trim().toLowerCase().replace(/\b\w/g, (ch) => ch.toUpperCase());
  let out = txt.replace(/\{first_name\}/g, full.split(" ")[0] || "").replace(/\{name\}/g, full);
  JOB_KEYS.forEach((k) => { if (job?.[k]) out = out.replaceAll(`{${k}}`, job[k]); });
  return out;
};

function AddTemplate({ onSaved, editing, onDone }) {
  const [name, setName] = useState(editing?.name || "");
  const [body, setBody] = useState(editing?.body || "");
  const save = async () => {
    try {
      if (editing) { await api.put(`/communications/templates/${editing.id}`, { name, channel: "sms", body }); }
      else { await api.post("/communications/templates", { name, channel: "sms", body }); }
      toast.success("Template saved"); setName(""); setBody(""); onSaved(); onDone?.();
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="border-t border-slate-100 pt-2 mt-2 space-y-2" data-testid="sms-template-add">
      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Template name" className="h-8 text-sm" data-testid="sms-template-name" />
      <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="Hi {first_name}, job {job_number} is booked for {delivery_date} with {engineer_name}." data-testid="sms-template-body"
        className="w-full px-2 py-1.5 text-sm rounded-md border border-slate-200" />
      <div className="flex gap-2">
        {editing && <Button size="sm" variant="outline" onClick={onDone} data-testid="sms-template-cancel">Cancel</Button>}
        <Button size="sm" className="flex-1 gap-1 bg-indigo-600 hover:bg-indigo-700 text-white" disabled={!name.trim() || !body.trim()} onClick={save} data-testid="sms-template-save">{editing ? <Pencil className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />} {editing ? "Save changes" : "Save template"}</Button>
      </div>
    </div>
  );
}

async function applyTemplate(t, customerName, jobId) {
  let job = null;
  if (jobId && JOB_KEYS.some((k) => t.body.includes(`{${k}}`))) {
    try { job = (await api.get(`/communications/jobs/${jobId}/template-fields`)).data; } catch { job = null; }
  }
  const text = fill(t.body, customerName, job);
  const left = JOB_KEYS.filter((k) => text.includes(`{${k}}`));
  if (left.length) toast.message(jobId ? `No ${left.map((k) => k.replace("_", " ")).join(", ")} on this job yet — please fill in` : "Choose a job under About to fill the job details");
  return text;
}

// One-tap chips for the starter templates (or the first three) — fills the box; staff still press Send.
export function QuickTemplateChips({ customerName, jobId, onPick, refreshKey }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/communications/templates").then((r) => {
      const sms = (r.data.items || []).filter((t) => (t.channel || "sms") === "sms");
      const starters = sms.filter((t) => t.starter);
      setItems((starters.length ? starters : sms).slice(0, 3));
    }).catch(() => {});
  }, [refreshKey]);
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap gap-2" data-testid="sms-quick-templates">
      {items.map((t) => (
        <button key={t.id} type="button" onClick={async () => onPick(await applyTemplate(t, customerName, jobId))} data-testid={`sms-quick-${t.id}`}
          className="rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700 transition-colors hover:bg-indigo-100">
          {t.name}
        </button>
      ))}
    </div>
  );
}

export function SmsTemplatePicker({ customerName, jobId, onPick, onChanged }) {
  const { can } = useAuth();
  const manage = can("communications.manage");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => { api.get("/communications/templates").then((r) => { setItems((r.data.items || []).filter((t) => (t.channel || "sms") === "sms")); onChanged?.(); }).catch(() => {}); }, [onChanged]);
  useEffect(() => { load(); }, [load]);
  const remove = async (t) => {
    try { await api.delete(`/communications/templates/${t.id}`); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const pick = async (t) => {
    onPick(await applyTemplate(t, customerName, jobId));
    setOpen(false);
  };
  if (!items.length && !manage) return null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" size="sm" variant="outline" className="gap-1.5" data-testid="sms-template-btn"><FileText className="w-3.5 h-3.5" /> Templates</Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-2" data-testid="sms-template-list">
        {!items.length && <p className="text-xs text-slate-400 px-2 py-2">No templates yet. Placeholders: {"{first_name}"}, {"{job_number}"}, {"{delivery_date}"}, {"{delivery_time}"}, {"{engineer_name}"}.</p>}
        <div className="max-h-64 overflow-y-auto space-y-1">
          {items.map((t) => (
            <div key={t.id} className="group flex items-start gap-1 rounded-md hover:bg-slate-50">
              <button type="button" className="flex-1 text-left px-2 py-1.5" onClick={() => pick(t)} data-testid={`sms-template-${t.id}`}>
                <div className="text-sm font-medium text-slate-800">{t.name}</div>
                <div className="text-xs text-slate-500 line-clamp-2">{t.body}</div>
              </button>
              {manage && <button type="button" onClick={() => setEditing(t)} className="p-1.5 text-slate-300 hover:text-indigo-600" title="Edit template" data-testid={`sms-template-edit-${t.id}`}><Pencil className="w-3.5 h-3.5" /></button>}
              {manage && <button type="button" onClick={() => remove(t)} className="p-1.5 text-slate-300 hover:text-rose-600" title="Delete template" data-testid={`sms-template-del-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
            </div>
          ))}
        </div>
        {manage && <AddTemplate key={editing?.id || "new"} editing={editing} onDone={() => setEditing(null)} onSaved={load} />}
      </PopoverContent>
    </Popover>
  );
}
